import type { ProgressStepDisplayState } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/types";
import type { ChatConversationMessage } from "@mirage/mosaics/Chat/types";
import { usePrevious } from "@mirage/shared/util/hooks";
import { useEffect, useState } from "react";
import styles from "./ConversationMessages.module.css";

export function useStepMessageTransitionClass(
  message: ChatConversationMessage,
  progressStepState: ProgressStepDisplayState | undefined,
) {
  const [currentState, setCurrentState] = useState<
    "hidden" | "hiding" | "shown"
  >(progressStepState === "hide" ? "hidden" : "shown");
  const previousProgressStepState = usePrevious(progressStepState);
  const previousMessage = usePrevious(message);

  useEffect(() => {
    if (message !== previousMessage) {
      // reset state if the message changes
      setCurrentState(progressStepState === "hide" ? "hidden" : "shown");
      return;
    }

    if (previousProgressStepState === progressStepState) {
      // no-op if progress-step state did not change
      return;
    }
    if (progressStepState === "hide") {
      if (currentState === "shown") {
        // fade out if we are currently shown
        setCurrentState("hiding");
        setTimeout(() => {
          setCurrentState("hidden");
        }, 1000);
      } else {
        // otherwise, immediately hide
        setCurrentState("hidden");
      }
    } else {
      setCurrentState("shown");
    }
  }, [
    currentState,
    message,
    previousMessage,
    previousProgressStepState,
    progressStepState,
  ]);

  switch (currentState) {
    case "hidden":
      return styles.ProgressStepHidden;
    case "hiding":
      return styles.ProgressStepHiding;
    case "shown":
      return "";
    default:
      currentState satisfies never;
  }
}

/**
 * @returns config about how this progress-step message should be displayed.
 * or undefined if this message is not a progress-step message.
 */
export function getProgressStepState(
  message: ChatConversationMessage,
  allMessages: ChatConversationMessage[],
  isWaitingForResponse: boolean,
): undefined | ProgressStepDisplayState {
  if (!isProgressStepMessage(message)) {
    return undefined;
  }

  // not waiting for a response. hide all progress-step messages.
  if (!isWaitingForResponse) {
    return "hide";
  }

  // this is the last message we are waiting reponse on
  if (allMessages[allMessages.length - 1] === message) {
    return "waiting";
  }

  // check if all messages after message are progress-step messages
  const indexOfMessage = allMessages.indexOf(message);
  if (allMessages.slice(indexOfMessage + 1).every(isProgressStepMessage)) {
    return "done";
  }

  // this is a progress-step message from the past, so we don't need to display it
  return "hide";
}

/**
 * @returns true if the message represents an update about a multi-step process.
 */
function isProgressStepMessage(message: ChatConversationMessage) {
  return (
    message.type === "message" &&
    (message.actionContext?.type === "dash_search" ||
      message.actionContext?.type === "dash_search_analyze")
  );
}
